import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { orgApi } from '../../lib/api';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Paintbrush, Image, Type, Save, Eye, RotateCcw, Upload, X } from 'lucide-react';
import { toast } from 'sonner';

// Resize an image file to a data URL (max dimension ~ 512px for logo/favicon, 1920 for bg)
const fileToResizedDataURL = (file, maxDim = 512, mime = 'image/png', quality = 0.9) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new window.Image();
      img.onload = () => {
        let { width, height } = img;
        const scale = Math.min(1, maxDim / Math.max(width, height));
        width = Math.round(width * scale);
        height = Math.round(height * scale);
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL(mime, quality));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

export default function BrandingPage() {
  const { user } = useAuth();
  const [orgs, setOrgs] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [branding, setBranding] = useState({
    primary_color: '#0A2342',
    secondary_color: '#0E7490',
    accent_color: '#1A56DB',
    logo_url: '',
    font_family: 'Outfit',
    login_bg_url: '',
    favicon_url: '',
    display_name: ''
  });
  const [saving, setSaving] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);

  useEffect(() => {
    loadOrgs();
  }, []);

  const loadOrgs = async () => {
    try {
      const res = await orgApi.list();
      const orgList = res.data || [];
      setOrgs(orgList);
      if (orgList.length > 0) {
        setSelectedOrg(orgList[0]);
        setBranding({
          primary_color: orgList[0].primary_color || '#0A2342',
          secondary_color: orgList[0].secondary_color || '#0E7490',
          accent_color: orgList[0].accent_color || '#1A56DB',
          logo_url: orgList[0].logo_url || '',
          font_family: orgList[0].font_family || 'Outfit',
          login_bg_url: orgList[0].login_bg_url || '',
          favicon_url: orgList[0].favicon_url || '',
          display_name: orgList[0].display_name || orgList[0].name
        });
      }
    } catch (err) {
      console.error('Failed to load orgs:', err);
    }
  };

  const handleSave = async () => {
    if (!selectedOrg) return;
    setSaving(true);
    try {
      await api.put(`/organizations/${selectedOrg.id}/branding`, branding);
      toast.success('Branding updated successfully');
    } catch (err) {
      toast.error('Failed to update branding');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setBranding({
      primary_color: '#0A2342',
      secondary_color: '#0E7490',
      accent_color: '#1A56DB',
      logo_url: '',
      font_family: 'Outfit',
      login_bg_url: '',
      favicon_url: '',
      display_name: selectedOrg?.name || ''
    });
  };

  const fonts = ['Outfit', 'Inter', 'Poppins', 'Roboto', 'Open Sans', 'Lato', 'Montserrat', 'Playfair Display', 'Merriweather'];

  // Upload helper — reads file, resizes, and stores result as data URL in the branding field
  const handleFileUpload = async (field, file, opts = {}) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be smaller than 5MB');
      return;
    }
    try {
      const dataUrl = await fileToResizedDataURL(
        file,
        opts.maxDim || 512,
        opts.mime || (file.type === 'image/png' ? 'image/png' : 'image/jpeg'),
        opts.quality || 0.9
      );
      setBranding(prev => ({ ...prev, [field]: dataUrl }));
      toast.success(`${opts.label || 'Image'} ready — click "Save Branding" to apply`);
    } catch (e) {
      console.error(e);
      toast.error('Failed to read image');
    }
  };

  const ImageUploadField = ({ label, field, maxDim, placeholder, accept = 'image/png,image/jpeg,image/svg+xml,image/webp', testId }) => {
    const inputRef = useRef(null);
    const val = branding[field] || '';
    return (
      <div>
        <Label>{label}</Label>
        <div className="flex gap-2 mt-1">
          <Input
            value={val}
            onChange={(e) => setBranding({ ...branding, [field]: e.target.value })}
            placeholder={placeholder}
            className="flex-1"
            data-testid={testId}
          />
          <input
            ref={inputRef}
            type="file"
            accept={accept}
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleFileUpload(field, f, { maxDim, label });
              if (inputRef.current) inputRef.current.value = '';
            }}
            data-testid={`${testId}-file-input`}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => inputRef.current?.click()}
            data-testid={`${testId}-upload-btn`}
          >
            <Upload className="w-4 h-4 mr-1" />Upload
          </Button>
          {val && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setBranding({ ...branding, [field]: '' })}
              data-testid={`${testId}-clear-btn`}
              title="Clear"
            >
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
        {val && (
          <div className="mt-2 p-2 bg-[#F8FAFC] rounded border border-[#E2E8F0] flex items-center gap-3">
            <img
              src={val}
              alt={label}
              className="h-12 w-12 object-contain bg-white rounded border border-[#E2E8F0]"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
            <div className="text-xs text-[#64748B] truncate flex-1">
              {val.startsWith('data:') ? `Uploaded image (${Math.round(val.length / 1024)} KB)` : val}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-8" data-testid="branding-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold text-[#0A2342]" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Organization Branding
          </h1>
          <p className="text-[#64748B] mt-1">Customize your organization's look and feel</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setPreviewMode(!previewMode)} data-testid="toggle-preview">
            <Eye className="w-4 h-4 mr-2" />
            {previewMode ? 'Edit Mode' : 'Preview'}
          </Button>
          <Button variant="outline" onClick={handleReset} data-testid="reset-branding">
            <RotateCcw className="w-4 h-4 mr-2" />
            Reset
          </Button>
          <Button className="bg-[#0E7490] hover:bg-[#0E7490]/90 text-white" onClick={handleSave} disabled={saving} data-testid="save-branding">
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save Branding'}
          </Button>
        </div>
      </div>

      {selectedOrg && (
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Branding Controls */}
          <div className="lg:col-span-2 space-y-6">
            {/* Colors */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Paintbrush className="w-5 h-5 text-[#0E7490]" />
                  Color Palette
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid sm:grid-cols-3 gap-4">
                  <div>
                    <Label>Primary Color</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <input
                        type="color"
                        value={branding.primary_color}
                        onChange={(e) => setBranding({ ...branding, primary_color: e.target.value })}
                        className="w-10 h-10 rounded cursor-pointer border-0"
                        data-testid="primary-color-picker"
                      />
                      <Input
                        value={branding.primary_color}
                        onChange={(e) => setBranding({ ...branding, primary_color: e.target.value })}
                        className="font-mono text-sm"
                        data-testid="primary-color-input"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Secondary Color</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <input
                        type="color"
                        value={branding.secondary_color}
                        onChange={(e) => setBranding({ ...branding, secondary_color: e.target.value })}
                        className="w-10 h-10 rounded cursor-pointer border-0"
                        data-testid="secondary-color-picker"
                      />
                      <Input
                        value={branding.secondary_color}
                        onChange={(e) => setBranding({ ...branding, secondary_color: e.target.value })}
                        className="font-mono text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Accent Color</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <input
                        type="color"
                        value={branding.accent_color || '#1A56DB'}
                        onChange={(e) => setBranding({ ...branding, accent_color: e.target.value })}
                        className="w-10 h-10 rounded cursor-pointer border-0"
                        data-testid="accent-color-picker"
                      />
                      <Input
                        value={branding.accent_color || '#1A56DB'}
                        onChange={(e) => setBranding({ ...branding, accent_color: e.target.value })}
                        className="font-mono text-sm"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Typography */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Type className="w-5 h-5 text-[#0E7490]" />
                  Typography
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Label>Font Family</Label>
                <select
                  value={branding.font_family}
                  onChange={(e) => setBranding({ ...branding, font_family: e.target.value })}
                  className="w-full mt-1 rounded-md border border-[#E2E8F0] p-2 text-sm"
                  data-testid="font-family-select"
                >
                  {fonts.map(f => (
                    <option key={f} value={f} style={{ fontFamily: f }}>{f}</option>
                  ))}
                </select>
                <div className="mt-4 p-4 bg-[#F8FAFC] rounded-lg">
                  <p className="text-2xl font-bold" style={{ fontFamily: branding.font_family }}>
                    {branding.display_name || selectedOrg.name}
                  </p>
                  <p className="text-sm text-[#64748B] mt-1" style={{ fontFamily: branding.font_family }}>
                    The quick brown fox jumps over the lazy dog
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Images */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Image className="w-5 h-5 text-[#0E7490]" />
                  Images & Assets
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Display Name</Label>
                  <Input
                    value={branding.display_name}
                    onChange={(e) => setBranding({ ...branding, display_name: e.target.value })}
                    placeholder="Organization display name"
                    className="mt-1"
                    data-testid="display-name-input"
                  />
                </div>
                <ImageUploadField
                  label="Logo"
                  field="logo_url"
                  maxDim={512}
                  placeholder="https://example.com/logo.png  — or click Upload"
                  testId="logo-url-input"
                />
                <ImageUploadField
                  label="Login Background Image"
                  field="login_bg_url"
                  maxDim={1920}
                  placeholder="https://example.com/background.jpg  — or click Upload"
                  testId="login-bg-url-input"
                />
                <ImageUploadField
                  label="Favicon"
                  field="favicon_url"
                  maxDim={128}
                  placeholder="https://example.com/favicon.ico  — or click Upload"
                  accept="image/png,image/jpeg,image/x-icon,image/vnd.microsoft.icon,image/svg+xml"
                  testId="favicon-url-input"
                />
              </CardContent>
            </Card>
          </div>

          {/* Live Preview */}
          <div>
            <Card className="sticky top-24">
              <CardHeader>
                <CardTitle className="text-lg">Live Preview</CardTitle>
              </CardHeader>
              <CardContent>
                {/* Mini header preview */}
                <div className="rounded-lg overflow-hidden border border-[#E2E8F0]">
                  <div className="p-3 flex items-center gap-2" style={{ backgroundColor: branding.primary_color }}>
                    {branding.logo_url ? (
                      <img src={branding.logo_url} alt="Logo" className="w-6 h-6 rounded" />
                    ) : (
                      <div className="w-6 h-6 rounded flex items-center justify-center text-xs font-bold text-white" style={{ backgroundColor: branding.secondary_color }}>
                        {(branding.display_name || selectedOrg.name).charAt(0)}
                      </div>
                    )}
                    <span className="text-white text-sm font-semibold" style={{ fontFamily: branding.font_family }}>
                      {branding.display_name || selectedOrg.name}
                    </span>
                  </div>
                  <div className="p-4 bg-[#F8FAFC]">
                    <div className="space-y-2">
                      <div className="h-3 rounded-full w-3/4" style={{ backgroundColor: branding.primary_color, opacity: 0.2 }} />
                      <div className="h-3 rounded-full w-1/2" style={{ backgroundColor: branding.secondary_color, opacity: 0.2 }} />
                    </div>
                    <div className="mt-3 flex gap-2">
                      <button className="px-3 py-1.5 rounded-lg text-white text-xs" style={{ backgroundColor: branding.secondary_color, fontFamily: branding.font_family }}>
                        Primary Button
                      </button>
                      <button className="px-3 py-1.5 rounded-lg text-xs border" style={{ color: branding.secondary_color, borderColor: branding.secondary_color, fontFamily: branding.font_family }}>
                        Secondary
                      </button>
                    </div>
                  </div>
                </div>

                {/* Login preview */}
                <div className="mt-4 rounded-lg overflow-hidden border border-[#E2E8F0]">
                  <div className="p-4 text-center" style={{
                    background: branding.login_bg_url
                      ? `url(${branding.login_bg_url}) center/cover`
                      : `linear-gradient(135deg, ${branding.primary_color}, ${branding.secondary_color})`
                  }}>
                    <div className="bg-white/95 rounded-lg p-4 max-w-[200px] mx-auto backdrop-blur-sm">
                      <p className="text-xs font-bold" style={{ color: branding.primary_color, fontFamily: branding.font_family }}>
                        {branding.display_name || selectedOrg.name}
                      </p>
                      <div className="mt-2 space-y-1">
                        <div className="h-5 bg-[#F1F5F9] rounded text-[8px] flex items-center px-2 text-[#94A3B8]">Email</div>
                        <div className="h-5 bg-[#F1F5F9] rounded text-[8px] flex items-center px-2 text-[#94A3B8]">Password</div>
                      </div>
                      <div className="mt-2 h-5 rounded text-[8px] text-white flex items-center justify-center" style={{ backgroundColor: branding.secondary_color }}>
                        Sign In
                      </div>
                    </div>
                  </div>
                </div>

                {/* Color swatches */}
                <div className="mt-4 flex gap-2">
                  <div className="flex-1 text-center">
                    <div className="w-full h-8 rounded" style={{ backgroundColor: branding.primary_color }} />
                    <p className="text-[10px] text-[#64748B] mt-1">Primary</p>
                  </div>
                  <div className="flex-1 text-center">
                    <div className="w-full h-8 rounded" style={{ backgroundColor: branding.secondary_color }} />
                    <p className="text-[10px] text-[#64748B] mt-1">Secondary</p>
                  </div>
                  <div className="flex-1 text-center">
                    <div className="w-full h-8 rounded" style={{ backgroundColor: branding.accent_color || '#1A56DB' }} />
                    <p className="text-[10px] text-[#64748B] mt-1">Accent</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}
